// Business settings (doc 26.5): every business number lives here, grouped, with a single save.
import { api } from "../lib/api";
import { fmt, t } from "../lib/i18n";
import { h, local, resource } from "../lib/ui";
import { act, Button, centsToInput, Chip, isBusy, Loading, LoadError, Toggle, toCents } from "../shared/components";
import { PageHead } from "./app";

interface Def {
  key: string;
  group: string;
  type: "percent" | "money" | "int" | "time" | "enum" | "bool" | "list";
  default: any;
  ref: string;
  unit?: string;
  options?: string[];
  nullable?: boolean;
  min?: number;
  max?: number;
}

interface Plan {
  key: string;
  name: { en: string; fr: string };
  priceCents: number;
  active: boolean;
  cleaning: "on_request" | "fixed_schedule";
  priorityQueue: boolean;
  sensorsAndLine: boolean;
}

function SettingInput(props: { def: Def; value: any; onChange: (v: any) => void; onInvalid: (bad: boolean) => void }) {
  const { def, value } = props;
  const id = `set-${def.key}`;
  const num = (raw: string, integer: boolean) => {
    if (raw.trim() === "") return def.nullable ? props.onChange(null) : props.onInvalid(true);
    const n = Number(raw.replace(",", "."));
    if (!Number.isFinite(n) || (integer && !Number.isInteger(n)) || n < (def.min ?? 0) || (def.max !== undefined && n > def.max)) return props.onInvalid(true);
    props.onInvalid(false);
    props.onChange(n);
  };
  switch (def.type) {
    case "percent":
      return (
        <div class="input-affix">
          <input class="input tabular" id={id} inputMode="decimal" value={String(value ?? "")} onInput={(e: Event) => num((e.target as HTMLInputElement).value, false)} />
          <span class="affix">%</span>
        </div>
      );
    case "money":
      return (
        <div class="input-affix">
          <span class="affix">$</span>
          <input class="input tabular" id={id} inputMode="decimal" value={centsToInput(value)} placeholder={def.nullable ? t("admin.settings.tbd") : ""}
            onInput={(e: Event) => {
              const raw = (e.target as HTMLInputElement).value;
              if (raw.trim() === "" && def.nullable) { props.onInvalid(false); return props.onChange(null); }
              const c = toCents(raw);
              if (c === null || c < 0) return props.onInvalid(true);
              props.onInvalid(false);
              props.onChange(c);
            }} />
        </div>
      );
    case "int":
      return (
        <div class={def.unit ? "input-affix" : ""}>
          <input class="input tabular" id={id} inputMode="numeric" value={String(value ?? "")} onInput={(e: Event) => num((e.target as HTMLInputElement).value, true)} />
          {def.unit ? <span class="affix">{t(`admin.settings.units.${def.unit}`)}</span> : null}
        </div>
      );
    case "time":
      return <input class="input tabular" id={id} type="time" value={value} onInput={(e: Event) => props.onChange((e.target as HTMLInputElement).value)} />;
    case "enum":
      return (
        <select class="select" id={id} value={value} onChange={(e: Event) => props.onChange((e.target as HTMLSelectElement).value)}>
          {def.options!.map((o) => <option value={o}>{t(`admin.settings.options.${o}`)}</option>)}
        </select>
      );
    case "bool":
      return <Toggle checked={!!value} onChange={props.onChange} label={value ? t("common.on") : t("common.off")} />;
    case "list":
      if (def.options) {
        return (
          <div class="row">
            {def.options.map((o) => (
              <label class="check">
                <input type="checkbox" checked={(value as string[]).includes(o)}
                  onChange={(e: Event) => {
                    const on = (e.target as HTMLInputElement).checked;
                    const next = on ? [...value, o] : (value as string[]).filter((x) => x !== o);
                    props.onInvalid(next.length === 0);
                    props.onChange(next);
                  }} />
                <span>{t(`languages.${o}`)}</span>
              </label>
            ))}
          </div>
        );
      }
      return (
        <textarea class="textarea" id={id} rows={2} style="min-height:60px" value={(value as string[]).join("\n")}
          onInput={(e: Event) => {
            const items = (e.target as HTMLTextAreaElement).value.split("\n").map((x) => x.trim()).filter(Boolean);
            props.onInvalid(items.length === 0);
            props.onChange(items);
          }} />
      );
  }
}

function PlansEditor(props: { plans: Plan[]; onChange: (p: Plan[]) => void }) {
  const set = (i: number, patch: Partial<Plan>) => props.onChange(props.plans.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th>{t("admin.settings.plans.nameEn")}</th>
            <th>{t("admin.settings.plans.nameFr")}</th>
            <th>{t("admin.settings.plans.price")}</th>
            <th>{t("admin.settings.plans.cleaning")}</th>
            <th>{t("admin.settings.plans.priority")}</th>
            <th>{t("admin.settings.plans.sensors")}</th>
            <th>{t("admin.settings.plans.active")}</th>
          </tr>
        </thead>
        <tbody>
          {props.plans.map((p, i) => (
            <tr key={p.key}>
              <td><input class="input" value={p.name.en} maxLength={40} aria-label={t("admin.settings.plans.nameEn")} onInput={(e: Event) => set(i, { name: { ...p.name, en: (e.target as HTMLInputElement).value } })} /></td>
              <td><input class="input" value={p.name.fr} maxLength={40} aria-label={t("admin.settings.plans.nameFr")} onInput={(e: Event) => set(i, { name: { ...p.name, fr: (e.target as HTMLInputElement).value } })} /></td>
              <td style="min-width:130px">
                <div class="input-affix">
                  <span class="affix">$</span>
                  <input class="input tabular" inputMode="decimal" value={centsToInput(p.priceCents)} aria-label={t("admin.settings.plans.price")}
                    onInput={(e: Event) => { const c = toCents((e.target as HTMLInputElement).value); if (c !== null && c >= 0) set(i, { priceCents: c }); }} />
                </div>
              </td>
              <td>
                <select class="select" value={p.cleaning} aria-label={t("admin.settings.plans.cleaning")} onChange={(e: Event) => set(i, { cleaning: (e.target as HTMLSelectElement).value as any })}>
                  <option value="on_request">{t("admin.settings.plans.onRequest")}</option>
                  <option value="fixed_schedule">{t("admin.settings.plans.fixedSchedule")}</option>
                </select>
              </td>
              <td><Toggle checked={p.priorityQueue} onChange={(v) => set(i, { priorityQueue: v })} /></td>
              <td><Toggle checked={p.sensorsAndLine} onChange={(v) => set(i, { sensorsAndLine: v })} /></td>
              <td><Toggle checked={p.active} onChange={(v) => set(i, { active: v })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SettingsPage() {
  const r = resource("admin.settings", () => api.get("/api/admin/settings"));
  const s = local("screen:settings", () => ({ tab: "commission", draft: {} as Record<string, any>, invalid: {} as Record<string, boolean> }));
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (!r.data) return <Loading rows={8} />;
  const { defs, groups, values, plans, meta } = r.data as { defs: Def[]; groups: string[]; values: Record<string, any>; plans: Plan[]; meta: Record<string, any> };
  const val = (k: string) => (k in s.draft ? s.draft[k] : k === "plans" ? plans : values[k]);
  const changedKeys = Object.keys(s.draft).filter((k) => JSON.stringify(s.draft[k]) !== JSON.stringify(k === "plans" ? plans : values[k]));
  const invalid = Object.values(s.invalid).some(Boolean);
  const setVal = (k: string, v: any) => s.set({ draft: { ...s.draft, [k]: v } });
  const tabs = [...groups];

  const save = () =>
    act("settings.save", async () => {
      const body: Record<string, any> = {};
      for (const k of changedKeys) body[k] = s.draft[k];
      const res = await api.put("/api/admin/settings", body);
      r.data = { ...r.data, values: res.values, plans: res.plans };
      s.set({ draft: {}, invalid: {} });
      r.reload();
    }, t("admin.settings.saved"));

  const groupDefs = defs.filter((d) => d.group === s.tab);
  const countIn = (g: string) => changedKeys.filter((k) => (k === "plans" ? g === "pricing" : defs.find((d) => d.key === k)?.group === g)).length;

  return (
    <div class="stack loose">
      <PageHead title={t("admin.settings.title")} intro={t("admin.settings.intro")} />
      <div class="settings-nav" role="tablist">
        {tabs.map((g) => (
          <button class={`btn small ${s.tab === g ? "" : "secondary"}`} role="tab" aria-selected={String(s.tab === g)} onClick={() => s.set({ tab: g })}>
            {t(`admin.settings.groups.${g}`)}
            {countIn(g) ? <span class="chip accent plain" style="min-height:18px;padding:0 7px">{countIn(g)}</span> : null}
          </button>
        ))}
      </div>

      {s.tab === "pricing" ? (
        <section class="panel">
          <div class="panel-head">
            <div>
              <h2>{t("admin.settings.plans.title")}</h2>
              <p class="small muted">{t("admin.settings.plans.intro")}</p>
            </div>
            <span class="xs muted">{t("admin.settings.docRef", { ref: "24.1" })}</span>
          </div>
          <PlansEditor plans={val("plans")} onChange={(p) => setVal("plans", p)} />
        </section>
      ) : null}

      <section class="panel">
        <div class="panel-head"><h2>{t(`admin.settings.groups.${s.tab}`)}</h2></div>
        <div>
          {groupDefs.map((d) => {
            const changed = changedKeys.includes(d.key);
            const m = meta[d.key];
            return (
              <div class={`setting-row ${changed ? "changed" : ""}`} key={d.key}>
                <div class="stack tight" style="gap:3px">
                  <label class="strong" htmlFor={`set-${d.key}`}>{t(`settings.${d.key}.label`)}</label>
                  <span class="small muted">{t(`settings.${d.key}.help`)}</span>
                  <span class="ref">
                    {t("admin.settings.docRef", { ref: d.ref })}
                    {m ? ` · ${t("admin.settings.lastChange", { who: m.updatedBy ?? "—", when: fmt.date(m.updatedAt) })}` : ""}
                  </span>
                </div>
                <div class="stack tight">
                  <SettingInput def={d} value={val(d.key)} onChange={(v) => setVal(d.key, v)} onInvalid={(bad) => s.set({ invalid: { ...s.invalid, [d.key]: bad } })} />
                  {s.invalid[d.key] ? <span class="xs" style="color:var(--c-critical)">{t("admin.settings.invalid")}</span> : null}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {changedKeys.length ? (
        <div class="savebar">
          <span>{t("admin.settings.unsaved", { count: changedKeys.length })}</span>
          <div class="row">
            <button class="btn ghost" style="color:inherit" onClick={() => s.set({ draft: {}, invalid: {} })}>{t("common.discard")}</button>
            <Button kind="accent" disabled={invalid} busy={isBusy("settings.save")} onClick={save}>{t("admin.settings.save")}</Button>
          </div>
        </div>
      ) : (
        <p class="xs muted row"><Chip kind="good" plain>{t("admin.settings.allSaved")}</Chip> {t("admin.settings.auditNote")}</p>
      )}
    </div>
  );
}
